import mParticleModule from '@mparticle/web-sdk';
import type { IdentityCallback, SDKInitConfig } from '@mparticle/web-sdk';
import type { IMParticleInstanceManager } from '@mparticle/web-sdk/internal';
// Registers the Rokt kit as an mParticle forwarder on eval. MUST stay after
// `@mparticle/web-sdk` — it reads the global the SDK import sets up.
import '@mparticle/web-rokt-kit';
import {
  isCateringOrderFlow,
  isMarketplacePlatform,
  isYelpPlatform,
} from '@local/utils';

// `IMParticleInstanceManager` (internal super-set of `MParticleWebSDK`) exposes
// `Rokt` for `mParticle.Rokt.selectPlacements(...)`; cast through `unknown` since
// the default export type satisfies neither. Export the module binding, not
// `window.mParticle`, to stay immune to third-party global reassignment.
export const mParticle =
  mParticleModule as unknown as IMParticleInstanceManager;

const DATA_PLAN_VERSION = 2;

const DIRECT_PLAN_ID = 'chownow_direct';
const MARKETPLACE_PLAN_ID = 'chownow_marketplace';
const YELP_PLAN_ID = 'chownow_obn';
const CATERING_PLAN_ID = 'chownow_catering';

const ALLOWED_URL_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'cn_channel',
];

/**
 * Returns allow-listed URL params to be attached as user attributes.
 * Yelp (OBN) sessions return an empty object — params aren't tracked there.
 * Marketplace only includes non-null values; Direct includes null values
 * to reset previously set attributes.
 */
export const getURLParams = (): Record<string, string | null> => {
  // We aren't tracking these params on Yelp
  if (isYelpPlatform()) {
    return {};
  }

  const url = new URL(window.location.href);
  const isMP = isMarketplacePlatform();
  const results: Record<string, string | null> = {};

  ALLOWED_URL_PARAMS.forEach((param) => {
    const val = url.searchParams.get(param);
    if (isMP) {
      // Don't include null query param values on Marketplace
      if (val) {
        results[param] = val;
      }
    } else {
      // Keep null values in order to reset them for Direct
      results[param] = val;
    }
  });

  if (document.referrer) {
    results.referrer = document.referrer;
  }

  return results;
};

// Order matters: Marketplace must be checked before Catering so MP-hosted
// catering URLs (e.g. www.chownow.com/catering/...) resolve to the MP plan.
export const getDataPlanId = (): string => {
  if (isMarketplacePlatform()) return MARKETPLACE_PLAN_ID;
  if (isYelpPlatform()) return YELP_PLAN_ID;
  if (isCateringOrderFlow()) return CATERING_PLAN_ID;
  return DIRECT_PLAN_ID;
};

export const getApiKey = (): string => {
  if (isMarketplacePlatform())
    return process.env.REACT_APP_MARKETPLACE_MPARTICLE_KEY ?? '';
  if (isYelpPlatform()) return process.env.REACT_APP_OBN_MPARTICLE_KEY ?? '';
  if (isCateringOrderFlow())
    return process.env.REACT_APP_CATERING_MPARTICLE_KEY ?? '';
  return process.env.REACT_APP_MPARTICLE_KEY ?? '';
};

const identityCallback: IdentityCallback = (result) => {
  const queryParams = getURLParams();
  const user = result.getUser();

  if (user) {
    user.setUserAttribute('web_app_version', `v${process.env.VERSION ?? ''}`);
    user.setUserAttribute(
      'global_privacy_control',
      // globalPrivacyControl is a non-standard browser API (GPC spec)
      !!(navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl
    );
    Object.keys(queryParams).forEach((attrKey) => {
      user.setUserAttribute(attrKey, queryParams[attrKey] ?? '');
    });
  }
};

let initialized = false;

export const initMParticle = (): void => {
  if (initialized) return;

  const apiKey = getApiKey();

  if (!apiKey) {
    // eslint-disable-next-line no-console
    console.warn(
      '[mParticle] No API key found for this platform — events will not be tracked.'
    );
    return;
  }

  initialized = true;

  const config: SDKInitConfig = {
    isDevelopmentMode: process.env.REACT_APP_DEPLOY_ENV !== 'prod',
    identityCallback,
    dataPlan: {
      planId: getDataPlanId(),
      planVersion: DATA_PLAN_VERSION,
    },
  };

  mParticle.init(apiKey, config);
};
